import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { NotificationPrefsDialog } from "@/components/app/NotificationPrefsDialog";
import { reopenGettingStarted } from "@/components/app/GettingStarted";
import { resetMiniTours } from "@/components/app/miniTours";
import { BellRing, ChevronDown, Compass, Home, ListChecks, LogOut, Settings } from "lucide-react";
import { toast } from "sonner";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/lib/auth";
import { ROLE_LABELS } from "@/lib/nav";

export const initials = (name = "") => name.split(" ").map((p) => p[0]).join("").slice(0, 2).toUpperCase();

export function UserMenu() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const qc = useQueryClient();
  const [prefs, setPrefs] = useState(false);
  const signOut = async () => {
    await logout();
    toast.success("You've been signed out");
    nav("/");
  };
  return (
    <>
    <NotificationPrefsDialog open={prefs} onOpenChange={setPrefs} />
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button data-testid="user-menu-trigger" className="flex items-center gap-2 rounded-lg px-2 py-1 hover:bg-subtle transition-colors duration-150">
          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-tint text-xs font-semibold text-brand">{initials(user.name)}</span>
          <span className="hidden text-left md:block">
            <span className="block text-sm font-medium leading-tight text-ink">{user.name}</span>
            <span className="block text-xs leading-tight text-ink-muted" data-testid="user-menu-role">{ROLE_LABELS[user.role]}</span>
          </span>
          <ChevronDown size={14} className="text-ink-muted" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56 rounded-[6px]">
        <DropdownMenuLabel className="font-normal">
          <p className="text-sm font-medium">{user.name}</p>
          <p className="text-xs text-ink-muted">{user.email}</p>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem data-testid="user-menu-getting-started" onClick={async () => { await reopenGettingStarted(qc); nav("/app"); }}>
          <ListChecks size={16} /> Getting started
        </DropdownMenuItem>
        <DropdownMenuItem data-testid="user-menu-notification-prefs" onClick={() => setPrefs(true)}>
          <BellRing size={16} /> Notification preferences
        </DropdownMenuItem>
        <DropdownMenuItem data-testid="user-menu-replay-tour" onClick={() => { resetMiniTours(user.id); nav("/app?tour=1"); }}>
          <Compass size={16} /> Replay tour
        </DropdownMenuItem>
        <DropdownMenuItem data-testid="user-menu-home" onClick={() => nav("/")}>
          <Home size={16} /> Home page
        </DropdownMenuItem>
        {user.role === "super_admin" && (
          <DropdownMenuItem data-testid="user-menu-settings" onClick={() => nav("/app/settings")}>
            <Settings size={16} /> Settings
          </DropdownMenuItem>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem data-testid="user-menu-sign-out" onClick={signOut} className="text-risk focus:text-risk">
          <LogOut size={16} /> Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
    </>
  );
}
